import type { KnowledgeArticle } from "../types"

export const aiAgentsVsAutomatisering: KnowledgeArticle = {
  id: "insight:ai-agents-vs-automatisering",
  pageType: "knowledge-article",
  slug: { nl: "ai-agents-vs-automatisering" },
  title: {
    nl: "AI Agents vs Traditionele Automatisering | Wanneer kies je wat?",
    en: "AI Agents vs Traditionele Automatisering | Wanneer kies je wat?",
  },
  description: {
    nl: "Wanneer is een AI-agent de juiste keuze en wanneer volstaan klassieke automatisering of scripting? Een technisch afwegingskader.",
    en: "When is an AI agent the right choice, and when do classic automation or scripting suffice? A technical decision framework.",
  },
  h1: {
    nl: "AI agents versus traditionele automatisering",
    en: "AI agents versus traditionele automatisering",
  },
  published: "2026-08-26",
  modified: "2026-08-26",
  author: "Camiel Schmeitz",
  sections: [
    {
      type: "paragraph",
      text: "Niet elk probleem heeft AI nodig, en niet elke AI-toepassing hoeft een agent te zijn. De keuze tussen klassieke automatisering en een AI-agent hangt af van één vraag: hoeveel interpretatie vraagt het proces?",
    },
    {
      type: "heading",
      text: "Klassieke automatisering: vaste regels, vaste stappen",
    },
    {
      type: "paragraph",
      text: "Scripts, schedulers en workflowtools zijn betrouwbaar, goedkoop voorspelbaar — zolang de input zich aan het formaat houdt. 'Als bestand binnenkomt met extensie X, verwerk dan stap Y' is geen werk voor een taalmodel. Hoe deterministischer het proces, hoe meer klassieke automatisering voor de hand ligt.",
    },
    {
      type: "heading",
      text: "Waar AI nodig is: variatie in inhoud",
    },
    {
      type: "paragraph",
      text: "Zodra de inhoud zelf geïnterpreteerd moet worden, stoten regelgebaseerde systemen op hun grens. Welke doelgroep past bij dit artikel? Gaat deze e-mail om een klacht of een vraag? Bij welke groep hoort deze situatie? Dat zijn interpretatieve vragen. Precies daar zit de meerwaarde van taalmodellen: zij lezen inhoud en trekken conclusies die met if-statements niet uit te drukken zijn.",
    },
    {
      type: "heading",
      text: "Agent of gewoon AI in de workflow?",
    },
    {
      type: "paragraph",
      text: "Ook dat onderscheid is belangrijk. Veel processen hebben maar één AI-stap nodig: classificeer dit document, vat deze tekst samen, haal deze velden eruit. Een volwaardige agent — die zelf beslist welke tools hij in welke volgorde aanroept — is pas nodig bij meerstaps-taken met variabele routes. Vuistregel: begin met de kleinste AI-component die het probleem oplost.",
    },
    {
      type: "heading",
      text: "Uit de praktijk",
    },
    {
      type: "paragraph",
      text: "Voor De Limburger bouwden we een systeem dat content automatisch categoriseert en plaatst over tientallen doelgroepen. De kracht zit precies op het raakvlak: de stappen van de workflow zijn vast en controleerbaar (automatisering), maar de beslissing 'welke content hoort waar' vereist inhoudelijk begrip (AI). Voor Burgerkracht was een volwaardige conversationele agent wél passend, omdat elk gesprek anders verloopt en de vervolgactie per gebruiker verschilt.",
    },
    {
      type: "callout",
      title: "Afwegingskader",
      text: "Vaste input + vaste stappen = script. Variabele inhoud + vaste stappen = AI-stap in de workflow. Variabele inhoud + variabele route = agent. Hoe verder naar rechts, hoe hoger de bouw- en beheerkosten.",
    },
    {
      type: "heading",
      text: "Wat kost het verschil?",
    },
    {
      type: "paragraph",
      text: "Klassieke automatisering heeft lage doorlopende kosten; AI introduceert kosten per verwerking en doorlopend kwaliteitsbeheer. Een agent vraagt daarnaast monitoring op gedrag. Die investering verdient zich terug zodra interpretatie écht waarde toevoegt — en is verspilling waar een script volstaat.",
    },
  ],
  relatedServiceIds: ["ai-automation", "ai-agents"],
  relatedCaseIds: ["de-limburger", "burgerkracht"],
}
